import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import { withRouter, useParams } from 'react-router-dom';

import Layout from 'components/Layout';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Wizard from 'components/UI/Wizard';
import { UserGroupsDetails, UserGroupsParents } from 'components/UserGroups/UserGroupsWizard';
import { uiActions } from 'state/modules/UI';
import { userGroupsActions } from 'state/modules/userGroups';
import { buildUrl } from 'utils';
import i18n from 'i18n-js';

import useStyles from './UserGroupsOnboarding.styled';

const UserGroupsOnboarding = ({ history }) => {
  const { id } = useParams();
  const classes = useStyles();
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const successMessage = `User Group ${id ? 'updated' : 'created'} successfully`;

  const stepsList = [
    {
      position: 1,
      key: 'details',
      label: 'Details',
    },
    {
      position: 2,
      key: 'parents',
      label: 'Parents',
    },
  ];

  const {
    loading,
    onboarding: { exitStep, position, exitOnboarding, errors, steps, lastStep },
  } = useSelector((state) => state.userGroups || {});

  const formRef = useRef(null);

  const dispatch = useDispatch();

  /**
   * Set title of the page
   */
  const setTitle = (vehicle) => {
    if (id && vehicle) {
      return `User Groups ID #${vehicle?.id}`;
    } else if (id) {
      return 'Update user groups info';
    }
    return 'Create user groups';
  };

  const onPrevious = () => {
    dispatch(userGroupsActions.changePosition('prev'));
  };

  const handleNextPosition = () => {
    dispatch(userGroupsActions.changePosition('next'));
  };

  const handleEndOnboarding = (showFeedback = true) => {
    dispatch(userGroupsActions.resetOnboarding());
    if (showFeedback) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: successMessage,
        })
      );
    }
    history.push(buildUrl('/users-groups'));
  };

  const handleNextStep = (exit = false) => {
    switch (position) {
      case 0:
        if (exit) {
          handleEndOnboarding(exit);
        } else if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      case 1:
        handleEndOnboarding(exit);
        break;
      default:
        break;
    }
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(userGroupsActions.setExit(exit || !exitStep, 'exitStep'));
  };

  const handleShowOnSaveAndExitModal = () => {
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (id) {
      dispatch(userGroupsActions.fetchUserGroupById(id, 'onboarding'));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (errors && lastStep && (Array.isArray(errors) || Object.keys(errors).length !== 0)) {
      handleEndOnboarding();
    } else if (lastStep) {
      displaySuccessModal();
    }
  }, [lastStep]);  // eslint-disable-line

  useEffect(() => {
    if (exitOnboarding) {
      handleEndOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  const displaySuccessModal = () => {
    handleEndOnboarding();
  };

  const getButtonText = () => {
    return position === 1
      ? i18n.t('common.finish')
      : id
      ? i18n.t('wizard.update_and_continue')
      : i18n.t('wizard.create_and_continue');
  };

  const userGroupsWizard = () => {
    return (
      <Box>
        <Wizard
          mode="create"
          exit={exitStep}
          steps={stepsList}
          loading={loading}
          title={setTitle(steps?.details)}
          position={position}
          handleExit={handleExit}
          onNext={handleNextStep}
          onPrevious={onPrevious}
          onSaveAndExit={onSaveAndExit}
          successMessage={successMessage}
          handleEndOnboarding={handleEndOnboarding}
          showSaveAndExitModal={showSaveAndExitModal}
          customStepperClass={classes.stepperContainer}
          handleShowOnSaveAndExitModal={handleShowOnSaveAndExitModal}
          saveButtonText={getButtonText()}
        >
          {position === 0 && (
            <UserGroupsDetails
              exit={exitStep}
              errors={errors}
              formRef={formRef}
              step={steps.details}
              type={id ? 'update' : 'create'}
              handleNextPosition={handleNextPosition}
            />
          )}
          {position === 1 && (
            <UserGroupsParents currentUserGroup={steps.details} step={steps.parents} />
          )}
        </Wizard>
      </Box>
    );
  };

  return (
    <Layout title="Dashboard">
      <Container maxwidth="lg">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginBottom: 48 }}
            >
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={24} my={1}>
                    {setTitle()}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {userGroupsWizard()}
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default withRouter(UserGroupsOnboarding);
